/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import type { CreateProjectParams, ProjectModel, UpdateProjectParams } from '@/domain/models/project.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';

type State = { projects: ProjectModel[]; isLoading: boolean; error: string | null };

export function useProjects(controller: ProjectController) {
    const { useProjectStore } = useApp();
    const setProjects = useProjectStore((s) => s.setProjects);
    const [state, setState] = useState<State>({ projects: [], isLoading: true, error: null });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');

    const load = useCallback(async () => {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const result = await controller.loadProjects({ search: search || undefined, status: statusFilter || undefined, clientId: clientFilter || undefined });
        result.fold(
            (error) => setState({ projects: [], isLoading: false, error: error.message }),
            (projects) => setState({ projects, isLoading: false, error: null })
        );
    }, [controller, search, statusFilter, clientFilter]);

    const loadMyProjects = useCallback(async () => {
        const result = await controller.loadMyProjects();
        result.fold(
            () => {},
            (projects) => setProjects(projects)
        );
        return result;
    }, [controller, setProjects]);

    useEffect(() => {
        load();
    }, [load]);

    const createProject = useCallback(
        async (params: CreateProjectParams) => {
            const result = await controller.createProject(params);
            if (result.isRight()) {
                await load();
            }
            return result;
        },
        [controller, load]
    );

    const updateProject = useCallback(
        async (id: string, params: UpdateProjectParams) => {
            const result = await controller.updateProject(id, params);
            if (result.isRight()) {
                await load();
            }
            return result;
        },
        [controller, load]
    );

    return { ...state, search, setSearch, statusFilter, setStatusFilter, clientFilter, setClientFilter, reload: load, loadMyProjects, createProject, updateProject };
}
